import {Button, Hotkey, IconButton} from '@wandb/components';
import React, {useEffect, useMemo} from 'react';
import {twMerge} from 'tailwind-merge';

import * as PanelBankSectionConfigTypes from '../../../state/views/panelBankSectionConfig/types';
import {getPanelSpec, PanelType} from '../../../util/panelTypes';
import {useWorkspaceDrawerContext} from '../../WorkspaceDrawer/WorkspaceDrawerContextProvider';
import {useGoToBaseUrl} from '../hooks/hooks';
import {usePanelContext} from '../PanelContextProvider';
import {FullScreenPanelEditButton} from '../PanelEditButton';
import {FullScreenPanelRunMetricButton} from '../PanelRunMetricButton';
import {useNextAndPreviousPanel} from '../useNextAndPreviousPanel';
import {FullScreenPanelOverflowMenu} from './FullScreenPanelOverflowMenu';

export const FullScreenPanelHeader = ({
  panelBankSectionConfigRef,
  panelType,
}: {
  panelBankSectionConfigRef: PanelBankSectionConfigTypes.Ref;
  panelType: PanelType;
}) => {
  const {panelRef, panelPopupMode} = usePanelContext();
  const goToBaseURL = useGoToBaseUrl();
  const workspaceDrawerContext = useWorkspaceDrawerContext();

  const panelSpec = useMemo(() => getPanelSpec(panelType), [panelType]);

  const {goToPreviousPanel, goToNextPanel, hasPreviousPanel, hasNextPanel} =
    useNextAndPreviousPanel({panelBankSectionConfigRef, panelRef});

  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        // Don't navigate back if a drawer is open - let the drawer handle ESC
        if (workspaceDrawerContext?.contentType) {
          return;
        }
        goToBaseURL();
      }
    };
    document.addEventListener('keydown', handleEscKey);
    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [goToBaseURL, panelPopupMode, workspaceDrawerContext?.contentType]);

  return (
    <div
      className={twMerge('shrink-0 flex flex-col', 'min-h-[32px]')}
      data-test="panel-header">
      <div className="flex w-full justify-between">
        <IconButton
          aria-label="Back"
          icon="back"
          variant="secondary"
          onPress={e => {
            e.continuePropagation();
            goToBaseURL();
          }}
        />

        <div className="flex gap-8">
          <Button
            aria-label="Previous panel"
            startIcon="chevron-back"
            onPress={e => {
              goToPreviousPanel();
              (e.target as HTMLElement).blur();
            }}
            isDisabled={!hasPreviousPanel}
            variant="secondary"
            tooltip={{
              children: (
                <>
                  <span style={{margin: '2px'}}>Previous panel</span>
                  <Hotkey keys={['←']} name="Previous panel" m={2} />
                </>
              ),
            }}>
            Previous
          </Button>
          <Button
            aria-label="Next panel"
            endIcon="chevron-next"
            onPress={e => {
              goToNextPanel();
              (e.target as HTMLElement).blur();
            }}
            isDisabled={!hasNextPanel}
            variant="secondary"
            tooltip={{
              children: (
                <>
                  <span style={{margin: '2px'}}>Next panel</span>
                  <Hotkey keys={['→']} name="Next panel" m={2} />
                </>
              ),
            }}>
            Next
          </Button>
        </div>
      </div>

      {/*
        NOTE: If this row is removed or its height changed, update the height
        calculations in 'PanelMediaBrowser'; see
        frontends/app/src/components/PanelMediaBrowser/PanelBody.tsx.
      */}
      <div className="mt-24 flex w-full justify-end">
        <div className="flex gap-8">
          <FullScreenPanelRunMetricButton
            panelBankSectionConfigRef={panelBankSectionConfigRef}
            panelRef={panelRef}
            panelType={panelType}
          />
          <FullScreenPanelEditButton />
          <FullScreenPanelOverflowMenu
            panelBankSectionConfigRef={panelBankSectionConfigRef}
            panelSpec={panelSpec}
          />
        </div>
      </div>
    </div>
  );
};
